/**
 * TxnAttentionBand.jsx — Phase 3
 *
 * Collapsible "Needs your attention" band that surfaces the two things
 * users care about most on a transactions review:
 *   1. Reconciliation candidates (statement rows we think should merge
 *      into an existing manual entry) — sourced from
 *      /api/reconciliation/pending which the parent fetches once on
 *      mount and passes in.
 *   2. Low-confidence categorisations — the parent filters the current
 *      txn list for confidenceOf(txn).key === 'low' and passes in that
 *      subset.
 *
 * The band collapses when there is nothing to surface (renders null).
 * Its collapsed-state preference is remembered in localStorage so a
 * user who dismisses it during one session sees it stay collapsed.
 */
import React, { useState, useEffect } from 'react';
import { AlertTriangle, ArrowDownLeft, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { prettifyDescription } from '@/lib/txDescription';

const LS_KEY = 'txn-attention-band-collapsed';

export default function TxnAttentionBand({
  reconciliationCount = 0,
  lowConfidence = [],   // array of tx rows filtered by parent
  unresolvedInflows = [],  // credits with no evidence of being income
  onOpenTxn,            // (id) => void — opens the drawer for that tx
}) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(LS_KEY) === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(LS_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const totalIssues = reconciliationCount + lowConfidence.length + unresolvedInflows.length;
  if (totalIssues === 0) return null;

  return (
    <section
      className="rounded-2xl border border-amber-500/30 bg-amber-50/40 dark:bg-amber-500/5 border-l-[3px] border-l-amber-500"
      data-testid="transactions-attention-band"
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3"
        aria-expanded={!collapsed}
        data-testid="transactions-attention-band-toggle"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-medium">Needs your attention</span>
          <Badge variant="outline" className="rounded-full text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-400">
            {totalIssues}
          </Badge>
        </div>
        {collapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 space-y-3">
          {/* Reconciliation candidates — the parent renders a full
              ReconciliationBanner above already, so here we just link
              across. Keeps this band focused on tx-level nudges. */}
          {reconciliationCount > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-xl bg-card border border-border/60 px-3 py-2" data-testid="transactions-attention-reconciliation-section">
              <div className="min-w-0">
                <div className="text-sm font-medium">{reconciliationCount} potential duplicate{reconciliationCount === 1 ? '' : 's'} found from statements</div>
                <div className="text-xs text-muted-foreground">These may be the same transactions you already added manually. Review before they double-count your spend.</div>
              </div>
              <Link to="/transactions?reconcile=1">
                <Button size="sm" variant="secondary" className="rounded-lg" data-testid="transactions-attention-reconciliation-cta">
                  Review
                </Button>
              </Link>
            </div>
          )}

          {/* Money in, meaning unknown. A credit proves cash arrived; it does
              not prove income. Kept out of income until the user says what it
              was, and their answer is remembered for them only. */}
          {unresolvedInflows.length > 0 && (
            <div data-testid="transactions-attention-unresolved-inflow-section" className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium px-1 mt-1">
                Money received · {unresolvedInflows.length}
              </div>
              <ul className="space-y-1.5">
                {unresolvedInflows.slice(0, 5).map((t) => {
                  const pretty = prettifyDescription(t.description);
                  const title = pretty.display || t.description || 'Credit';
                  return (
                    <li key={t.id} className="flex items-center justify-between gap-3 rounded-xl bg-card border border-border/60 px-3 py-2">
                      <div className="min-w-0 flex items-center gap-2">
                        <ArrowDownLeft className="h-3.5 w-3.5 text-sky-600 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{title}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            Money received — confirm what it was
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm" variant="secondary" className="rounded-lg shrink-0"
                        onClick={() => onOpenTxn?.(t.id)}
                        data-testid={`transactions-attention-confirm-inflow-${t.id}`}
                      >
                        Confirm
                      </Button>
                    </li>
                  );
                })}
              </ul>
              {unresolvedInflows.length > 5 && (
                <div className="text-[11px] text-muted-foreground px-1">
                  + {unresolvedInflows.length - 5} more below
                </div>
              )}
            </div>
          )}

          {/* Low-confidence categorisation list — first 5 for scannability. */}
          {lowConfidence.length > 0 && (
            <div data-testid="transactions-attention-low-confidence-section" className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium px-1 mt-1">
                Low-confidence categorisations · {lowConfidence.length}
              </div>
              <ul className="space-y-1.5">
                {lowConfidence.slice(0, 5).map((t) => {
                  const pretty = prettifyDescription(t.description);
                  const title = pretty.display || t.category || 'Transaction';
                  return (
                    <li key={t.id} className="flex items-center justify-between gap-3 rounded-xl bg-card border border-border/60 px-3 py-2">
                      <div className="min-w-0 flex items-center gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{title}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            Currently: <span className="font-medium">{t.category || 'Miscellaneous'}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm" variant="ghost" className="rounded-lg shrink-0"
                        onClick={() => onOpenTxn?.(t.id)}
                        data-testid={`transactions-attention-fix-${t.id}`}
                      >
                        Fix
                      </Button>
                    </li>
                  );
                })}
              </ul>
              {lowConfidence.length > 5 && (
                <div className="text-[11px] text-muted-foreground px-1">
                  + {lowConfidence.length - 5} more below
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
